import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
  DocumentsLieSkeletonRow,
  enrichDocumentsLieSkeletonRows$,
} from './document-linked-documents.util';
import type { DocumentService } from './document.service';

export type StockTransfertErpLinkedRefDoc = {
  erp_document_id?: number;
  type_document_id?: number;
  type_label?: string;
  code?: string | null;
  link?: {
    kind?: string;
    path: string;
    queryParams?: Record<string, string>;
  } | null;
};

export type StockTransfertErpLinkedRefsItem = {
  stock_transfert_num_document: string;
  error?: string;
  lc_transfert?: {
    lc_transfert_id?: number | null;
    num_document?: string | null;
  } | null;
  erp_bon_sortie?: StockTransfertErpLinkedRefDoc | null;
  erp_pv_ecart?: StockTransfertErpLinkedRefDoc | null;
  erp_bon_transfert?: StockTransfertErpLinkedRefDoc | null;
  erp_bon_reception?: StockTransfertErpLinkedRefDoc | null;
};

export type LcReceptionErpWorkflowLinkRow = {
  code: string;
  typeLabel: string;
  erpDocumentId?: number;
  link?: { path: string; queryParams?: Record<string, string> };
};

export function lookupStockTransfertErpLinkedRefsEntry(
  items: Record<string, StockTransfertErpLinkedRefsItem>,
  numDocument: string
): StockTransfertErpLinkedRefsItem | null {
  const num = String(numDocument ?? '').trim();
  if (!num || num === '—' || !items || typeof items !== 'object') {
    return null;
  }
  const direct = items[num];
  if (direct) {
    return direct;
  }
  const digits = num.replace(/\D/g, '');
  if (digits.length > 0) {
    const padded = digits.padStart(8, '0');
    if (items[padded]) {
      return items[padded];
    }
    for (const key of Object.keys(items)) {
      const kd = key.replace(/\D/g, '');
      if (kd === digits || kd.endsWith(digits) || digits.endsWith(kd)) {
        return items[key];
      }
    }
  }
  const values = Object.values(items);
  return values.length === 1 ? values[0] : null;
}

function erpRefToLcLinkRow(
  doc: StockTransfertErpLinkedRefDoc | null | undefined,
  typeLabel: string,
  lcTransfertId: number
): LcReceptionErpWorkflowLinkRow | null {
  const code = erpLinkedRefToDisplayCode(doc);
  if (!code) {
    return null;
  }
  const id = Number(doc?.erp_document_id);
  let link: LcReceptionErpWorkflowLinkRow['link'];
  if (doc?.link?.path) {
    link = {
      path: doc.link.path,
      queryParams: { ...(doc.link.queryParams ?? {}) },
    };
    if (typeLabel === 'Bon de réception' && Number.isFinite(lcTransfertId) && lcTransfertId > 0) {
      link.queryParams = {
        ...link.queryParams,
        readonly: link.queryParams?.['readonly'] ?? '1',
        lcTransfertId: String(lcTransfertId),
      };
    }
    if (typeLabel === 'Bon de transfert' && Number.isFinite(lcTransfertId) && lcTransfertId > 0) {
      link.queryParams = {
        ...link.queryParams,
        source: link.queryParams?.['source'] ?? 'lc',
        lcTransfertId: String(lcTransfertId),
      };
    }
  }
  return {
    typeLabel,
    code,
    ...(Number.isFinite(id) && id > 0 ? { erpDocumentId: id } : {}),
    ...(link ? { link } : {}),
  };
}

/** Liste réception LC : bon de sortie + PV d'écart ERP liés au transfert. */
export function mapStockTransfertErpLinkedRefsItemToLcWorkflowLinks(
  entry: StockTransfertErpLinkedRefsItem | null | undefined,
  lcTransfertId: number
): LcReceptionErpWorkflowLinkRow[] {
  if (!entry || entry.error) {
    return [];
  }
  const rows: LcReceptionErpWorkflowLinkRow[] = [];
  for (const [doc, label] of [
    [entry.erp_bon_sortie, 'Bon de sortie'],
    [entry.erp_pv_ecart, "PV d'écart"],
  ] as const) {
    const row = erpRefToLcLinkRow(doc, label, lcTransfertId);
    if (row) {
      rows.push(row);
    }
  }
  return rows;
}

export function mapLcReceptionErpWorkflowLinksToDocumentLieSkeletons(
  links: LcReceptionErpWorkflowLinkRow[]
): DocumentsLieSkeletonRow[] {
  return (links || []).map((row) => ({
    type: row.typeLabel,
    reference: row.code,
    date: '--',
    montant: 0,
    statut: '--',
    _lcWorkflowLink: row.link,
    erpDocumentId:
      Number.isFinite(Number(row.erpDocumentId)) && Number(row.erpDocumentId) > 0
        ? Number(row.erpDocumentId)
        : erpIdFromLcWorkflowLink(row.link),
  }));
}

function erpIdFromLcWorkflowLink(
  link: LcReceptionErpWorkflowLinkRow['link'] | undefined
): number | null {
  const id = Number(link?.queryParams?.['id']);
  return Number.isFinite(id) && id > 0 ? id : null;
}

/** Tableau popup réception LC : BS + PV avec date / montant / statut ERP. */
export function loadLcReceptionDocumentsLiesTableRows$(
  documentService: DocumentService,
  numDocument: string,
  lcTransfertId: number,
  stationId?: number | null
) {
  return loadLcReceptionErpLinkedRows$(documentService, numDocument, lcTransfertId, stationId).pipe(
    switchMap((links) =>
      enrichDocumentsLieSkeletonRows$(
        documentService,
        mapLcReceptionErpWorkflowLinksToDocumentLieSkeletons(links),
        null
      )
    )
  );
}

export function erpLinkedRefToDisplayCode(
  doc: StockTransfertErpLinkedRefDoc | null | undefined
): string {
  const code = String(doc?.code ?? '').trim();
  return code && code !== '—' ? code : '';
}

/** Bon de sortie + PV d'écart ERP depuis num_document stock_transfert LC. */
export function loadLcReceptionErpLinkedRows$(
  documentService: DocumentService,
  numDocument: string,
  lcTransfertId: number,
  stationId?: number | null
) {
  const num = String(numDocument ?? '').trim();
  if (!num || num === '—') {
    return of([]);
  }
  const lcId = Number(lcTransfertId);
  return documentService
    .postStockTransfertErpLinkedRefs({
      num_document: num,
      ...(stationId != null && stationId > 0 ? { station_id: stationId } : {}),
    })
    .pipe(
      map((items) => {
        const entry = lookupStockTransfertErpLinkedRefsEntry(items, num);
        return mapStockTransfertErpLinkedRefsItemToLcWorkflowLinks(
          entry,
          Number.isFinite(lcId) && lcId > 0 ? lcId : 0
        );
      }),
      catchError(() => of([]))
    );
}
